import { Link } from "react-router-dom";
import { SEO, serviceSchema, breadcrumbSchema } from "@/components/seo";
import { CTASection } from "@/components/common/CTASection";
import { FiArrowRight, FiCheck, FiTrendingDown, FiHome, FiDollarSign, FiRefreshCw } from "@/lib/icons";
import { broker } from "@/config/broker.config";

const reasons = [
  {
    icon: <FiTrendingDown className="size-6" />,
    title: "Lower your rate",
    description:
      "Even a small rate reduction can save thousands over the life of your loan. We compare your current loan against 70+ lenders.",
  },
  {
    icon: <FiHome className="size-6" />,
    title: "Access equity",
    description:
      "If your property value has increased, you may be able to access equity for renovations, investments, or other purposes.",
  },
  {
    icon: <FiDollarSign className="size-6" />,
    title: "Consolidate debt",
    description:
      "Roll high-interest debts like credit cards into your home loan at a much lower rate. Just be mindful of the longer term.",
  },
  {
    icon: <FiRefreshCw className="size-6" />,
    title: "Better features",
    description:
      "Switch to a loan with better features like an offset account, redraw facility, or more flexible repayment options.",
  },
];

const process = [
  {
    step: 1,
    title: "Free comparison",
    description:
      "We compare your current loan against products from 70+ lenders to identify potential savings.",
  },
  {
    step: 2,
    title: "Cost-benefit analysis",
    description:
      "We calculate all costs involved and show you the break-even point to ensure refinancing makes sense.",
  },
  {
    step: 3,
    title: "Application",
    description:
      "If it's worth it, we prepare and submit your application, handling all lender communication.",
  },
  {
    step: 4,
    title: "Settlement",
    description:
      "The new lender pays out your old loan. We coordinate everything to ensure a smooth transition.",
  },
];

const warningSignsData = [
  "Your rate is more than 0.5% above current market rates",
  "You've been with the same lender for 3+ years without a rate review",
  "Your fixed rate period is ending soon",
  "Your property has increased significantly in value",
  "Your income has improved since you first borrowed",
  "You're paying high-interest debts alongside your mortgage",
  "Your current loan lacks features you need (offset, redraw)",
  "You've received cashback or rate offers from other lenders",
];

export function RefinancingPage() {
  return (
    <>
      <SEO
        title="Refinance Home Loan Brisbane, Springfield & Logan"
        description="Refinance your home loan in Brisbane, Springfield, Logan or Ipswich. Compare 70+ lenders to lower your rate, access equity or consolidate debt."
        url="/home-loans/refinancing"
        keywords={[
          "refinance Brisbane",
          "refinance Springfield",
          "refinance Logan",
          "refinance Ipswich",
          "home loan refinance",
          "mortgage refinance Brisbane",
          "cash out refinance",
        ]}
        jsonLd={[
          serviceSchema({
            name: "Home Loan Refinancing",
            description:
              "Refinance home loans to lower rates, access equity, consolidate debt or change loan features. Compared from 70+ lenders.",
            url: "/home-loans/refinancing",
            serviceType: "Home Loan Refinancing",
          }),
          breadcrumbSchema([
            { name: "Home", url: "/" },
            { name: "Home Loans", url: "/home-loans" },
            { name: "Refinancing", url: "/home-loans/refinancing" },
          ]),
        ]}
      />

      {/* Hero */}
      <section className="bg-cream py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <Link
                to="/home-loans"
                className="inline-flex items-center text-sm text-muted-foreground hover:text-primary mb-6 transition-colors"
              >
                <FiArrowRight className="size-4 mr-2 rotate-180" />
                Home Loans
              </Link>
              <p className="section-label mb-4">Refinancing</p>
              <h1 className="font-serif text-4xl md:text-5xl lg:text-6xl mb-6">
                Could you be paying less?
              </h1>
              <p className="text-lg text-muted-foreground mb-8">
                Refinancing could save you thousands - or help you access equity in
                your home. We'll compare your current loan against 70+ lenders to
                find out if there's a better deal.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-full font-medium hover:bg-primary/90 transition-colors"
                >
                  Get Free Comparison
                  <FiArrowRight className="size-4" />
                </Link>
                <Link
                  to="/guides/refinancing-guide"
                  className="inline-flex items-center gap-2 border border-border px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                >
                  Read Guide
                </Link>
              </div>
            </div>
            <div className="bg-primary text-primary-foreground rounded-3xl p-8">
              <p className="text-primary-foreground/90 text-sm uppercase tracking-wider mb-2">
                Average savings
              </p>
              <p className="font-serif text-5xl mb-2">$3,000+</p>
              <p className="text-primary-foreground/90 text-sm mb-6">
                per year on a 0.5% rate reduction ($600K loan)
              </p>
              <div className="border-t border-primary-foreground/20 pt-6">
                <p className="text-sm text-primary-foreground/80">
                  Many borrowers are paying more than they need to. A quick
                  comparison could reveal significant savings.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Reasons */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">Why Refinance</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              Reasons to consider refinancing
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            {reasons.map((reason) => (
              <div
                key={reason.title}
                className="flex items-start gap-4 p-6 bg-muted/30 rounded-2xl"
              >
                <div className="p-3 bg-white rounded-xl text-primary flex-shrink-0">
                  {reason.icon}
                </div>
                <div>
                  <h3 className="font-semibold text-lg mb-2">{reason.title}</h3>
                  <p className="text-muted-foreground text-sm">
                    {reason.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Warning Signs */}
      <section className="py-16 md:py-20 bg-cream">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-start">
            <div>
              <p className="section-label mb-4">Self-Assessment</p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                Signs it might be time to refinance
              </h2>
              <p className="text-muted-foreground mb-6">
                If any of these apply to you, it's worth getting a free comparison
                to see if you could be paying less.
              </p>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-2.5 rounded-full font-medium hover:bg-primary/90 transition-colors"
              >
                Get free comparison
                <FiArrowRight className="size-4" />
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-6 border border-border/50">
              <div className="space-y-3">
                {warningSignsData.map((sign) => (
                  <div key={sign} className="flex items-start gap-3">
                    <div className="w-5 h-5 border-2 border-primary rounded flex items-center justify-center flex-shrink-0 mt-0.5">
                      <FiCheck className="size-3 text-primary" />
                    </div>
                    <span className="text-sm text-muted-foreground">{sign}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="text-center mb-12">
            <p className="section-label mb-4">The Process</p>
            <h2 className="font-serif text-3xl md:text-4xl mb-4">
              How refinancing works
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Most refinances complete within 2-4 weeks. We handle the heavy lifting
              so you can focus on your day.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {process.map((item) => (
              <div key={item.step} className="bg-muted/30 rounded-2xl p-6">
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-serif text-lg mb-4">
                  {item.step}
                </div>
                <h3 className="font-semibold mb-2">{item.title}</h3>
                <p className="text-sm text-muted-foreground">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Costs */}
      <section className="py-16 md:py-20 bg-primary text-primary-foreground">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="section-label mb-4">
                Costs
              </p>
              <h2 className="font-serif text-3xl md:text-4xl mb-6">
                What does refinancing cost?
              </h2>
              <p className="text-primary-foreground/80 mb-6">
                There are some costs involved in refinancing. We'll calculate these
                for you and show you the break-even point to ensure it's worth it.
              </p>
              <ul className="space-y-3">
                {[
                  { item: "Discharge fee (old lender)", cost: "$150 - $400" },
                  { item: "Application fee (new lender)", cost: "Often waived" },
                  { item: "Valuation fee", cost: "Often waived" },
                  { item: "Settlement fee", cost: "$200 - $500" },
                ].map((cost) => (
                  <li key={cost.item} className="flex items-center justify-between">
                    <span className="text-primary-foreground/80">{cost.item}</span>
                    <span className="font-medium">{cost.cost}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-white/10 rounded-2xl p-6">
              <p className="text-primary-foreground text-sm font-medium mb-2">Important</p>
              <h3 className="font-semibold text-xl mb-4">
                Fixed rate break costs
              </h3>
              <p className="text-primary-foreground/80 text-sm">
                If you're currently on a fixed rate, break costs can be significant
                - sometimes tens of thousands of dollars. We'll always check this
                before recommending refinancing and factor it into our analysis.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <CTASection
        label="Is it time to refinance?"
        title="Get a free comparison and see how much you could save"
        description="No obligation, no cost. We'll show you the numbers so you can decide."
        primaryAction={{ label: "Get Comparison", href: "/contact" }}
        secondaryAction={{
          label: "Call Us",
          href: `tel:${broker.phone.replace(/\s/g, "")}`,
        }}
      />
    </>
  );
}
